"use client";

import Image from "next/image";
import Link from "next/link";
import { Session } from "next-auth";
import { useSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { TwoStageTooltip } from "@/components/ui/common/TwoStageTooltip";
import { UserProfileDropdown } from "@/components/ui/layouts/UserProfileDropdown";
import { api } from "@/trpc/react";
import { SystemAlertBanner } from "@/components/layout/SystemAlertBanner";

import { MobileFeedbackButton } from "@/components/ui/feedback/FeedbackFAB";
import { Button } from "@/components/ui/shadcn/button";
import {
  NotificationBell,
  useNotifications,
  type Notification,
} from "@/components/ui/notifications";
import { InfoSplashModal } from "@/components/ui/news/InfoSplashModal";
import { NavbarQuotaIndicator } from "@/components/ui/dashboard/NavbarQuotaIndicator";
import { ThemeToggle } from "@/components/ui/common/ThemeToggle";

// Navbar height in Tailwind units (1 unit = 0.25rem = 4px)
export const NAVBAR_HEIGHT = 18; // 18 * 4px = 72px

interface NavbarProps {
  session: Session | null;
}

interface NewsAnnouncement {
  id: string;
  title: string;
  content: string;
  severity: "info" | "warning" | "critical";
  version: number;
  publishedAt: string | null;
  createdAt: string;
}

export function Navbar({ session }: NavbarProps) {
  const { data: liveSession, status: sessionStatus } = useSession();
  const [newsModalOpen, setNewsModalOpen] = useState(false);
  const [modalAnnouncements, setModalAnnouncements] = useState<NewsAnnouncement[]>([]);
  const headerRef = useRef<HTMLElement>(null);
  const currentSession = sessionStatus === "authenticated" ? liveSession : session;

  // Notification center state
  const {
    notifications,
    showRead,
    onToggleShowRead,
    onMarkAsRead,
    onMarkAllAsRead,
    onClearToasts,
    isLoading,
  } = useNotifications();

  // Fetch news for modal
  const { data: unreadNewsData } = api.news.getUnread.useQuery(
    { limit: 20, offset: 0 },
    { enabled: !!currentSession }
  );
  const { data: activeNewsData } = api.news.getActive.useQuery(
    { limit: 20, offset: 0 },
    { enabled: !!currentSession }
  );
  const markNewsAsReadMutation = api.news.markAsRead.useMutation();

  // Handle clicking on news notification
  const handleNotificationClick = async (notification: Notification) => {
    if (notification.kind === "news" && notification.meta?.kind === "news") {
      const announcementId = notification.meta.announcementId;
      const isUnread = notification.readAt === null;

      // If unread, show all unread announcements with clicked one first
      // If already read, show just that single announcement
      let orderedAnnouncements: NewsAnnouncement[] = [];

      if (isUnread) {
        // Get all unread announcements at this moment (before marking as read)
        const allUnread = unreadNewsData?.announcements ?? [];

        // Find the clicked announcement and put it first in the list
        const clickedIndex = allUnread.findIndex((a) => a.id === announcementId);
        orderedAnnouncements = [...allUnread];

        if (clickedIndex > -1) {
          // Move clicked announcement to the front
          const clicked = orderedAnnouncements.splice(clickedIndex, 1)[0];
          orderedAnnouncements = clicked
            ? [clicked, ...orderedAnnouncements]
            : orderedAnnouncements;
        }

        // Mark as read after capturing the list
        try {
          await markNewsAsReadMutation.mutateAsync({ announcementId });
          onMarkAsRead(`news-${announcementId}`);
        } catch (error) {
          console.error("Failed to mark news as read:", error);
        }
      } else {
        // For already-read news, show just that single announcement
        const allActive = activeNewsData?.announcements ?? [];
        const clickedAnnouncement = allActive.find((a) => a.id === announcementId);
        orderedAnnouncements = clickedAnnouncement ? [clickedAnnouncement] : [];
      }

      // Store the announcements for the modal
      setModalAnnouncements(orderedAnnouncements);
      setNewsModalOpen(true);
    }
  };

  // Handle acknowledging news in modal
  const handleNewsAcknowledge = async (announcementId: string) => {
    try {
      await markNewsAsReadMutation.mutateAsync({ announcementId });
      onMarkAsRead(`news-${announcementId}`);
    } catch (error) {
      console.error("Failed to mark news as read:", error);
    }
  };

  const handleNewsModalClose = () => {
    setNewsModalOpen(false);
    setModalAnnouncements([]);
  };

  const { data: systemAlert } = api.system.getAlert.useQuery(undefined, {
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (query.state.data?.enabled ? 300000 : false),
    refetchIntervalInBackground: false,
    enabled: !!currentSession,
  });

  const isAdmin = currentSession?.user?.isAdmin ?? false;

  const pathname = usePathname();
  const isAuthPage = pathname === "/auth/signin" || pathname === "/auth/account-suspended";

  // Detect if we're on a workspace page (pathname is just a UUID).
  // The regex matches only the root workspace path — if sub-routes are ever added under
  // [sessionId]/ (e.g. /uuid/settings), update this to /^\/uuid(?:\/.*)?$/ and adjust callers.
  const workspaceSessionId = (() => {
    const uuidPattern = /^\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
    const match = pathname?.match(uuidPattern);
    return match?.[1] ?? null;
  })();

  useEffect(() => {
    const updateHeaderOffset = () => {
      const height = headerRef.current?.offsetHeight ?? 72;
      document.documentElement.style.setProperty("--app-header-offset", `${height}px`);
    };

    updateHeaderOffset();
    window.addEventListener("resize", updateHeaderOffset);

    return () => {
      window.removeEventListener("resize", updateHeaderOffset);
      document.documentElement.style.setProperty("--app-header-offset", "72px");
    };
  }, [
    pathname,
    currentSession,
    systemAlert?.enabled,
    systemAlert?.title,
    systemAlert?.description,
  ]);

  if (isAuthPage) return null;

  return (
    <header
      ref={headerRef}
      className="relative z-50 border-b border-border bg-background opacity-90"
    >
      {currentSession && <SystemAlertBanner alert={systemAlert} />}
      <div className="relative">
        {/* K logo — white SVG, inverted to black in light mode via CSS */}
        <style>{`.navbar-logo { filter: invert(1); } .dark .navbar-logo { filter: none; }`}</style>
        <div className="absolute left-0 top-0 z-10 flex h-[72px] w-[72px] items-center justify-center">
          <Link
            href="/"
            className="flex h-12 w-12 items-center justify-center rounded-full transition-all duration-150 hover:rounded-2xl hover:bg-accent"
            aria-label="Go to home"
          >
            <Image
              src="/Kimara_logo_mark_white.svg"
              alt="Kimara"
              width={28}
              height={28}
              className="navbar-logo"
            />
          </Link>
        </div>
        <div
          className={`mx-auto flex h-${NAVBAR_HEIGHT} items-center justify-between px-4 sm:px-6 lg:p-8`}
        >
          {/* Spacer matching the absolutely-positioned K-icon */}
          <div className="w-12 flex-shrink-0" />
          <div className="flex items-center gap-2 text-sm md:gap-3">
            {session && !isAuthPage ? (
              <>
                {/* Usage & quota indicator (icon + popover) */}
                <NavbarQuotaIndicator />

                {isAdmin && (
                  <TwoStageTooltip label="Admin" hint="Dashboard & controls" side="bottom">
                    <Link
                      href="/admin"
                      className="flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                      aria-label="Admin"
                    >
                      <ShieldCheck className="h-5 w-5" />
                    </Link>
                  </TwoStageTooltip>
                )}

                {/* Feedback button - visible in navbar */}
                <MobileFeedbackButton />

                {/* Notification Bell */}
                <NotificationBell
                  notifications={notifications}
                  showRead={showRead}
                  onToggleShowRead={onToggleShowRead}
                  onMarkAsRead={onMarkAsRead}
                  onMarkAllAsRead={onMarkAllAsRead}
                  onClearToasts={onClearToasts}
                  onNotificationClick={handleNotificationClick}
                  isLoading={isLoading}
                />

                <ThemeToggle compact />

                <UserProfileDropdown
                  session={currentSession}
                  workspaceSessionId={workspaceSessionId}
                />
              </>
            ) : !session && !isAuthPage ? (
              <div className="flex items-center gap-4 md:gap-8">
                <Link
                  href="/pricing"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Pricing
                </Link>
                <Link
                  href="/guides"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Guides
                </Link>
                <Link
                  href="/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  API Docs
                </Link>
                <ThemeToggle compact />
                <Button
                  variant="primary"
                  size="lg"
                  asChild
                  className="relative overflow-hidden px-3 md:px-6 py-2.5  shadow-xl transition-all duration-200 hover:scale-105 hover:shadow-2xl"
                  style={{
                    background:
                      "linear-gradient(135deg, hsl(244 100% 65%) 0%, hsl(249 100% 62%) 100%)",
                    boxShadow:
                      "0 0 30px rgba(89, 76, 255, 0.5), 0 4px 20px -8px rgba(89, 76, 255, 0.8)",
                  }}
                >
                  <Link href="/auth/signin">
                    <span className="relative z-10 font-semibold md:font-bold text-sm md:text-base">
                      Sign In
                    </span>
                    {/* Animated gradient overlay on hover */}
                    <div
                      className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300"
                      style={{
                        background:
                          "linear-gradient(135deg, hsl(249 100% 62%) 0%, hsl(244 100% 65%) 100%)",
                      }}
                    />
                  </Link>
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* News modal - shown when clicking on news notifications */}
      {session && (
        <InfoSplashModal
          open={newsModalOpen}
          announcements={modalAnnouncements}
          onAcknowledgeSingle={handleNewsAcknowledge}
          onClose={handleNewsModalClose}
          mode="carousel"
        />
      )}
    </header>
  );
}
